import { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Select, MenuItem, FormControl, InputLabel,
  Chip, CircularProgress, Alert, Grid, Card, CardContent, Snackbar,
} from '@mui/material';
import { Add as AddIcon, Send as SendIcon, Preview as PreviewIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import { api } from '../services/apiClient';
import { API_ENDPOINTS } from '../config/api';
import { useEnum, ENUM_NAMES } from '../hooks/useEnum';

interface Campaign {
  id: number;
  name: string;
  campaign_type: string;
  channel: string;
  status: string;
  total_recipients: number;
  sent_count: number;
  scheduled_date: string | null;
  sent_date: string | null;
  created_by_name: string;
  created_at: string;
}

const statusColor = (s: string): 'default' | 'info' | 'warning' | 'success' | 'error' => {
  if (s === 'Draft') return 'default';
  if (s === 'Scheduled') return 'info';
  if (s === 'Sending') return 'warning';
  if (s === 'Sent') return 'success';
  if (s === 'Cancelled') return 'error';
  return 'default';
};

const emptyForm = {
  name: '',
  campaign_type: 'Vaccination',
  channel: 'email',
  subject_line: '',
  message_template: '',
  scheduled_date: '',
  species: '',
  vaccine_name: '',
  days_overdue_min: '0',
};

const RecallCampaignsPage = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [snackbar, setSnackbar] = useState('');
  const [deleteCampaign, setDeleteCampaign] = useState<Campaign | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { values: campaignTypes } = useEnum(ENUM_NAMES.RECALL_CAMPAIGN_TYPES);
  const { values: recallChannels } = useEnum(ENUM_NAMES.RECALL_CHANNELS);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await api.get<{ data: Campaign[] }>(API_ENDPOINTS.RECALL_CAMPAIGNS.LIST);
      setCampaigns(Array.isArray(res.data) ? res.data : []);
    } catch {
      setError('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name) { setError('Campaign name is required'); return; }
    try {
      setSaving(true);
      const target_criteria: Record<string, unknown> = {};
      if (formData.species) target_criteria.species = formData.species;
      if (formData.vaccine_name) target_criteria.vaccine_name = formData.vaccine_name;
      if (formData.days_overdue_min) target_criteria.days_overdue_min = parseInt(formData.days_overdue_min);

      await api.post(API_ENDPOINTS.RECALL_CAMPAIGNS.CREATE, {
        name: formData.name,
        campaign_type: formData.campaign_type,
        channel: formData.channel,
        subject_line: formData.subject_line || undefined,
        message_template: formData.message_template || undefined,
        scheduled_date: formData.scheduled_date || undefined,
        target_criteria: Object.keys(target_criteria).length ? target_criteria : undefined,
      });
      setDialogOpen(false);
      setFormData(emptyForm);
      fetchData();
    } catch {
      setError('Failed to create campaign');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async (id: number) => {
    try {
      const res = await api.post<{ count: number }>(API_ENDPOINTS.RECALL_CAMPAIGNS.PREVIEW(id), {});
      setSnackbar(`Preview: ${res.count} matching clients found`);
    } catch {
      setError('Failed to preview campaign');
    }
  };

  const handleSend = async (id: number) => {
    try {
      const res = await api.post<{ total_recipients: number }>(API_ENDPOINTS.RECALL_CAMPAIGNS.SEND(id), {});
      setSnackbar(`Campaign sent to ${res.total_recipients} recipients`);
      fetchData();
    } catch {
      setError('Failed to send campaign');
    }
  };

  const handleDelete = async () => {
    if (!deleteCampaign) return;
    setDeleting(true);
    try {
      await api.delete(API_ENDPOINTS.RECALL_CAMPAIGNS.DELETE(deleteCampaign.id));
      setDeleteCampaign(null);
      fetchData();
    } catch { setError('Failed to delete campaign'); } finally { setDeleting(false); }
  };

  const totalCampaigns = campaigns.length;
  const draftCount = campaigns.filter(c => c.status === 'Draft').length;
  const sentThisMonth = campaigns.filter(c => {
    const today = new Date().toISOString().slice(0, 7);
    return c.status === 'Sent' && c.sent_date?.slice(0, 7) === today;
  }).length;

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
      <CircularProgress />
    </Box>
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Recall Campaigns</Typography>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card><CardContent>
            <Typography color="textSecondary">Total Campaigns</Typography>
            <Typography variant="h4">{totalCampaigns}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card><CardContent>
            <Typography color="textSecondary">Drafts</Typography>
            <Typography variant="h4" color="text.secondary">{draftCount}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card><CardContent>
            <Typography color="textSecondary">Sent This Month</Typography>
            <Typography variant="h4" color="success.main">{sentThisMonth}</Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      <Box sx={{ mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
          New Campaign
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Channel</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Recipients</TableCell>
              <TableCell>Scheduled</TableCell>
              <TableCell>Sent</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {campaigns.map(c => (
              <TableRow key={c.id} hover>
                <TableCell><strong>{c.name}</strong></TableCell>
                <TableCell>{c.campaign_type}</TableCell>
                <TableCell><Chip label={c.channel} size="small" variant="outlined" /></TableCell>
                <TableCell><Chip label={c.status} size="small" color={statusColor(c.status)} /></TableCell>
                <TableCell align="right">{c.total_recipients || '-'}</TableCell>
                <TableCell>{c.scheduled_date ? new Date(c.scheduled_date).toLocaleDateString() : '-'}</TableCell>
                <TableCell>{c.sent_date ? new Date(c.sent_date).toLocaleDateString() : '-'}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {(c.status === 'Draft' || c.status === 'Scheduled') && (
                      <>
                        <Button size="small" startIcon={<PreviewIcon />} onClick={() => handlePreview(c.id)}>Preview</Button>
                        <Button size="small" variant="contained" color="primary" startIcon={<SendIcon />} onClick={() => handleSend(c.id)}>Send</Button>
                        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleteCampaign(c)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {campaigns.length === 0 && (
              <TableRow><TableCell colSpan={8} align="center">No campaigns yet</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Recall Campaign</DialogTitle>
        <DialogContent>
          <TextField fullWidth margin="normal" label="Campaign Name *" value={formData.name}
            onChange={e => setFormData({ ...formData, name: e.target.value })} />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Type</InputLabel>
                <Select value={formData.campaign_type} label="Type"
                  onChange={e => setFormData({ ...formData, campaign_type: e.target.value })}>
                  {campaignTypes.map(t => <MenuItem key={t.code} value={t.display_name}>{t.display_name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Channel</InputLabel>
                <Select value={formData.channel} label="Channel"
                  onChange={e => setFormData({ ...formData, channel: e.target.value })}>
                  {recallChannels.map(ch => <MenuItem key={ch.code} value={ch.code}>{ch.display_name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <TextField fullWidth margin="normal" label="Subject Line" value={formData.subject_line}
            onChange={e => setFormData({ ...formData, subject_line: e.target.value })} />
          <TextField fullWidth margin="normal" label="Message Template" multiline rows={3}
            value={formData.message_template}
            onChange={e => setFormData({ ...formData, message_template: e.target.value })} />
          <TextField fullWidth margin="normal" label="Scheduled Date" type="date"
            InputLabelProps={{ shrink: true }} value={formData.scheduled_date}
            onChange={e => setFormData({ ...formData, scheduled_date: e.target.value })} />

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Target Criteria</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField fullWidth label="Species" placeholder="e.g. Canine" value={formData.species}
                onChange={e => setFormData({ ...formData, species: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Vaccine Name" placeholder="e.g. Rabies" value={formData.vaccine_name}
                onChange={e => setFormData({ ...formData, vaccine_name: e.target.value })} />
            </Grid>
          </Grid>
          <TextField fullWidth margin="normal" label="Min Days Overdue" type="number"
            value={formData.days_overdue_min}
            onChange={e => setFormData({ ...formData, days_overdue_min: e.target.value })}
            helperText="Include patients whose vaccine is overdue by at least this many days" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Create Draft'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteCampaign} onClose={() => setDeleteCampaign(null)}>
        <DialogTitle>Delete Campaign</DialogTitle>
        <DialogContent>
          <Typography>Delete campaign "<strong>{deleteCampaign?.name}</strong>"?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteCampaign(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>{deleting ? 'Deleting...' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar('')}
        message={snackbar}
      />
    </Box>
  );
};

export default RecallCampaignsPage;
